import Vue from 'vue';
import { v4 as uuidv4 } from 'uuid'
import { getGridItemsForLayout, regenerateLayoutIds, sanitizeGridItems } from '@/utils/gridUtils';
import store from "@/store";
import NotificationContent from "@/components/toastification/NotificationContent.vue";

// Constants
const DEFAULT_TAB_PREFIX = 'Tab';
const DEFAULT_LAYOUT_TYPES = ['default', 'statistics', 'unit-live', 'unit-recording', 'unit-cameras', 'alarm-dmp-logs', 'maintenance-view'];

const getTabSafely = (state, tabId = null) => {
  const targetTabId = tabId || state.activeTab;
  return state.tabs && targetTabId ? state.tabs[targetTabId] : null;
};

const updateTabOrders = (tabs) => {
  Object.keys(tabs).forEach((key, index) => {
    tabs[key].order = index + 1;
    const tabOrderRegex = new RegExp(`${DEFAULT_TAB_PREFIX}\\ [\\d]+`);
    if (tabOrderRegex.test(tabs[key].name)) {
      tabs[key].name = `${DEFAULT_TAB_PREFIX} ${tabs[key].order}`;
    }
  });
};

const findLayoutByIdOrName = (userLayouts, layoutValue) => {
  return userLayouts.find(layout => layout.id === layoutValue) ||
    userLayouts.find(layout =>
      String(layout.name).toLowerCase() === String(layoutValue).toLowerCase()
    );
};

const createTabOptions = (slots, rootGetters) => ({
  slots: slots || [],
  rootGetters,
});

const state = {
  activeTab: "tab_1",
  tabs: {
    tab_1: {
      name: "Tab 1",
      layout: 1,
      slots: [],
      type: 'default',
      order: '1',
      gridItems: [],
      hiddenSlots: [],
    },
  },
};

const getters = {
  tabs(state) {
    return state.tabs;
  },
  activeTabId(state) {
    return state.tabs ? state.activeTab : null;
  },
  currentTab(state) {
    return getTabSafely(state);
  },
  currentTabSlots(state) {
    const currentTab = getTabSafely(state);
    return currentTab ? currentTab.slots : [];
  },
  tmpLayoutItems(state) {
    return state.tmpLayoutItems;
  },
  currentTabGridItems(state) {
    const currentTab = getTabSafely(state);
    return currentTab ? currentTab.gridItems : [];
  },
  currentTabLayoutId(state) {
    const currentTab = getTabSafely(state);
    return currentTab ? currentTab.layout : null;
  },
  currentTabName(state) {
    const currentTab = getTabSafely(state);
    return currentTab ? currentTab.name : '';
  },
  getSideBarItem(state) {
    return (id) => {
      return (
        state.sideBarItems.find((item) => item.id === id) ||
        state.bottomBarItems.find((item) => item.id === id)
      );
    };
  },
};

const mutations = {
  setActiveTab(state, value) {
    state.activeTab = value;
  },
  clearAlarmTabs(state) {
    if (!state.tabs) return;

    Object.entries(state.tabs).forEach(([tabId, tab]) => {
      if (tab?.context?.isAlarm) {
        Vue.delete(state.tabs, tabId);
      }
    });
    updateTabOrders(state.tabs);

    // The active tab may have been one of the removed alarm tabs
    if (!state.tabs[state.activeTab]) {
      state.activeTab = Object.keys(state.tabs)[0] || null;
    }
  },
  addNewTab(state, value) {
    if (Array.isArray(state.tabs)) state.tabs = {};
    if (state.tabs) {
      const tabsLength = Object.keys(state.tabs).length;
      Vue.set(state.tabs, value.uuid, {
        name: value.name,
        layout: value.layoutId,
        slots: [],
        type: value.type,
        order: tabsLength + 1,
        color: value.color,
        context: value.context, // useful for describe a tab
        gridItems: value.gridItems || [],
        hiddenSlots: [],
      });
      updateTabOrders(state.tabs);
      Object.keys(state.tabs).forEach((key, index) => {
        state.tabs[key].order = index + 1;
        if (/Tab\ [\d]+/.test(state.tabs[key].name)) {
          state.tabs[key].name = `Tab ${state.tabs[key].order}`;
        }
      });
    }
  },
  updateTabLayout(state, { tabId, layoutId }) {
    const targetTab = state.tabs[tabId];
    if (targetTab) {
      targetTab.layout = layoutId;
    }
  },
  removeTab(state, tabId) {
    Vue.delete(state.tabs, tabId);
    updateTabOrders(state.tabs);
  },
  setTab(state, value) {
    // Clean overlapping widgets from saved configs so GridStack can't overflow.
    if (value && typeof value === 'object') {
      Object.values(value).forEach(tab => {
        if (tab && Array.isArray(tab.gridItems) && tab.gridItems.length) {
          tab.gridItems = sanitizeGridItems(tab.gridItems);
        }
      });
    }
    state.tabs = value;
  },
  updateTabType(state, { id, type }) {
    const targetTab = state.tabs[id];
    if (targetTab) {
      targetTab.type = type;
    }
  },
  editTabName(state, { data: { id, name } }) {
    const targetTab = state.tabs[id];
    if (targetTab) {
      targetTab.name = name;
    }
  },
  addTabView(state, value) {
    Vue.set(state.tabs, value.id, {
      name: value.name,
      layout: value.layout,
      slots: [...value.slots],
      type: value.type,
      order: value.order,
      gridItems: value.gridItems || [],
    });
  },
  updateTabLayoutGrid(state, { tabId, gridItems }) {
    const targetTab = state.tabs[tabId];
    if (targetTab) {
      targetTab.gridItems = gridItems;
    }
  },
  setTabVideoTourState(state, { tabId, videoTour }) {
    const targetTab = state.tabs[tabId];
    if (!targetTab) return;

    if (videoTour) {
      Vue.set(targetTab, 'videoTour', {
        ...(targetTab.videoTour || {}),
        ...videoTour,
      });
    } else if (Object.prototype.hasOwnProperty.call(targetTab, 'videoTour')) {
      Vue.delete(targetTab, 'videoTour');
    }
  },
  removeSlotById(state, { tabId, slotId }) {
    const targetTab = state.tabs[tabId];
    if (targetTab && targetTab.slots) {
      targetTab.slots = targetTab.slots.filter(slot => slot.id !== slotId);
    }
  },
  removeGridItemById(state, { tabId, gridItemId }) {
    const targetTab = state.tabs[tabId];
    if (!targetTab || !targetTab.gridItems) return;

    const itemToRemove = targetTab.gridItems.find(item => item.id === gridItemId);
    if (!itemToRemove) return;

    const removedPosition = itemToRemove.pos;
    const updatedGridItems = targetTab.gridItems
      .filter(item => item.id !== gridItemId)
      .map(item => item.pos > removedPosition ? { ...item, pos: item.pos - 1 } : item);

    targetTab.gridItems = updatedGridItems;

    if (targetTab.slots) {
      targetTab.slots = targetTab.slots.map(slot =>
        slot.pos > removedPosition ? { ...slot, pos: slot.pos - 1 } : slot
      );
    }
  },
  syncSlotsWithGridItems(state, { tabId }) {
    const targetTab = state.tabs[tabId];
    if (!targetTab) return;

    const gridItems = targetTab.gridItems || [];
    const currentSlots = targetTab.slots || [];

    if (currentSlots.length > 0) {
      const gridItemMap = gridItems.reduce((map, item) => {
        map[item.id] = item;
        return map;
      }, {});

      targetTab.slots = currentSlots.map(slot => {
        const matchingItem = gridItemMap[slot.id];
        return matchingItem ? {
          ...slot,
          pos: matchingItem.pos,
          x: matchingItem.x,
          y: matchingItem.y,
          w: matchingItem.w,
          h: matchingItem.h
        } : slot;
      });
    }
  }
};

const actions = {
  setActiveTab({ commit, state }, value) {
    const tabExists = Object.keys(state.tabs).includes(value);
    const fallbackTab = Object.keys(state.tabs)[0];
    commit('setActiveTab', tabExists ? value : fallbackTab);
  },
  addNewTab({ commit, dispatch, state, getters, rootGetters }, data = null) {
    return new Promise(resolve => {
      const tabName = data?.tabName;
      const tabContext = data?.tabContext;
      const existingTab = tabName ?
        Object.values(state.tabs).find(t => t.name === tabName) :
        undefined;

      const tabsLength = Object.keys(state.tabs).length;
      const maxTabs = store.getters["app/maxTabs"];
      if (tabsLength >= maxTabs) {

        Vue.$toast({
          component: NotificationContent,
          position: "top-right",
          timeout: 10000,
          props: {
            title: 'tabs.alert.title',
            variant: "danger",
            icon: "AlertCircleIcon",
            text: 'tabs.alert.text',
            params: { max: maxTabs },
          },
        });

        return resolve({ status: false, id: "" });
      }

      // Avoid duplicated tabs
      const tab =
        data && data.tabName
          ? Object.values(state.tabs).find((t) => t.name === data.tabName)
          : undefined;

      if (existingTab) {
        resolve({ status: false, id: '' });
        return;
      }

      const uuid = uuidv4();
      const defaultLayoutId = getters.userLayouts[0]?.id || 1; // Use default layout (id: 1) if no user layouts

      // Special case for videowall
      if (data?.viewData) {
        let viewData = data.viewData;
        if (!viewData.gridItems || viewData.gridItems.length === 0) {
          const options = createTabOptions(viewData.slots, rootGetters);

          if (viewData.layout === 'standalone') {
            viewData = { ...viewData, gridItems: viewData.slots };
          } else {
            let gridItems = getGridItemsForLayout(viewData.layout, options);
            // Keep widget ids unique across tabs to avoid GridStack collisions
            gridItems = regenerateLayoutIds(gridItems, viewData.slots);
            viewData = { ...viewData, gridItems };
          }
        }

        // Clean overlapping widgets from saved view data
        viewData = { ...viewData, gridItems: sanitizeGridItems(viewData.gridItems) };
        commit('addTabView', viewData);
        dispatch('updateUserConfig');
      } else {
        let selectedLayout;

        if (data?.layout) {
          if (DEFAULT_LAYOUT_TYPES.includes(data.layout)) {
            selectedLayout = data.layout;
          } else {
            const layout = findLayoutByIdOrName(getters.userLayouts, data.layout);
            selectedLayout = layout ? layout.id : selectedLayout;
          }
        }

        const type = data?.type || 'default'; // The type of the tab: default, statistics, unit-live... just to help identify the tab
        const layoutId = selectedLayout ? (selectedLayout).toString() : defaultLayoutId;
        const options = createTabOptions(data?.slots, rootGetters);
        let gridItems = getGridItemsForLayout(layoutId, options);
        // Layout templates ship with hardcoded ids; regenerate them so each tab
        // has unique widget ids
        gridItems = regenerateLayoutIds(gridItems, data?.slots);

        commit('addNewTab', {
          uuid,
          name: tabName,
          context: tabContext,
          layoutId,
          type,
          gridItems,
          color: data?.tabColor ?? data?.color,
        });
        dispatch('updateUserConfig');
      }

      resolve({ status: true, id: uuid });
    });
  },
  clearAlarmTabs({ commit, dispatch }) {
    commit('clearAlarmTabs');
    dispatch('updateUserConfig');
  },
  removeTab({ commit, dispatch, rootState }, data) {
    if (rootState.grid.targetTabId === data && rootState.grid.active) {
      dispatch('stopVideoTour');
    }
    commit('removeTab', data);
    dispatch('updateUserConfig');
  },
  setTabs({ commit }, value) {
    commit("setTabs", value);
  },
  setTab({ commit }, value) {
    commit("setTab", value);
  },
  updateTabType({ commit }, value) {
    commit("updateTabType", value);
  },
  editTabName({ commit, dispatch }, data) {
    commit('editTabName', data);
    dispatch('updateUserConfig');
  },
  removeSlotById({ commit, dispatch }, { tabId, slotId }) {
    return new Promise((resolve) => {
      commit('removeSlotById', { tabId, slotId });
      dispatch('updateUserConfig');
      resolve({ status: true });
    });
  },
  removeItemById({ commit, dispatch }, { tabId, itemId }) {
    return new Promise((resolve) => {
      commit('removeSlotById', { tabId, slotId: itemId });
      commit('removeGridItemById', { tabId, gridItemId: itemId });
      dispatch('updateUserConfig');
      resolve({ status: true });
    });
  },
  syncSlotsWithGridItems({ commit, dispatch }, { tabId }) {
    commit('syncSlotsWithGridItems', { tabId });
    dispatch('updateUserConfig');
  }
};

export default {
  state,
  getters,
  mutations,
  actions,
};
